import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type FontSizeOption = 'medium' | 'large' | 'small';

@Injectable({
  providedIn: 'root'
})
export class FontSizeService {
  // Default size adjustment percentage
  private fontSizePercentage = 10;

  // Observable source for font size changes
  private fontSizeSubject = new BehaviorSubject<FontSizeOption>('medium');
  public fontSize$ = this.fontSizeSubject.asObservable();

  // CSS variable name that will be set on the root element
  private readonly cssVarName = '--app-font-size-scale';

  constructor() {
    // Initialize from localStorage if available
    const savedSize = localStorage.getItem('appFontSize') as FontSizeOption;
    if (savedSize) {
      this.setFontSize(savedSize);
    } else {
      this.setFontSize('medium');
    }
  }

  /**
   * Set the font size option
   */
  setFontSize(size: FontSizeOption): void {
    this.fontSizeSubject.next(size);
    localStorage.setItem('appFontSize', size);

    // Apply the CSS variable to the document root
    const scale = this.getFontSizeScale(size);
    document.documentElement.style.setProperty(this.cssVarName, scale);

    console.log(`Font size set to ${size} (scale: ${scale})`);
  }

  /**
   * Get the current font size option
   */
  getCurrentFontSize(): FontSizeOption {
    return this.fontSizeSubject.getValue();
  }

  /**
   * Set the adjustment percentage
   */
  setFontSizePercentage(percentage: number): void {
    this.fontSizePercentage = percentage;
    // Re-apply current setting with new percentage
    this.setFontSize(this.getCurrentFontSize());
  }

  /**
   * Get font size adjustment percentage
   */
  getFontSizePercentage(): number {
    return this.fontSizePercentage;
  }

  /**
   * Get the font size factor (multiplier) for compatibility with existing code
   */
  getFontSizeFactor(): number {
    const size = this.getCurrentFontSize();
    switch (size) {
      case 'large':
        return 1 + (this.fontSizePercentage / 100);
      case 'small':
        return 1 - (this.fontSizePercentage / 100);
      default:
        return 1;
    }
  }

  /**
   * Convert size option to CSS scale value
   */
  private getFontSizeScale(size: FontSizeOption): string {
    switch (size) {
      case 'large':
        return `${1 + (this.fontSizePercentage / 100)}`;
      case 'small':
        return `${1 - (this.fontSizePercentage / 100)}`;
      default:
        return '1';
    }
  }
}
